import React, { useState, useEffect, useContext, useMemo, useCallback, useRef } from 'react';
import axios from "@common/axios";
import { debounce } from 'lodash';
import {
    Form,
    Input,
    InputNumber,
    Select,
    Checkbox,
    Button,
    Row,
    Col,
    Tree,
    Typography,
    message,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { BrandContext } from '@context/BrandContext';
import { ContentContext } from '@context/ContentContext';
import CircularLoader from "@common/CircularLoader";
import {useBreadcrumbComponent} from "@common/useBreadcrumbComponent";

const { Title, Paragraph, Text } = Typography;
const { Option } = Select;

const TestWizard = ({ onClose, onTestGenerated }) => {
    const [form] = Form.useForm();
    const brandConfig = useContext(BrandContext);
    const { content } = useContext(ContentContext);
    const treeContainerRef = useRef(null);

    const [loadingTree, setLoadingTree] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [countingQuestion, setCountingQuestion] = useState(false);
    const [treeData, setTreeData] = useState([]);
    const [checkedKeys, setCheckedKeys] = useState([]);
    const [checkedNodes, setCheckedNodes] = useState([]);
    const [questionCount, setQuestionCount] = useState(0);
    const [canGenerate, setCanGenerate] = useState(false);
    const [expandedKeys, setExpandedKeys] = useState([]);
    const [activeKey, setActiveKey] = useState(null); // For keyboard navigation, starts as null
    const [isTreeFocused, setIsTreeFocused] = useState(false); // Tracks if the tree container has focus
    const BreadcrumbBar = useBreadcrumbComponent({ dropEndingSeparator: true });

    const getVisibleNodes = () => {
        const visibleNodes = [];
        const traverse = (nodes) => {
            if (!nodes) return;
            for (const node of nodes) {
                visibleNodes.push(node);
                if (expandedKeys.includes(node.key) && node.children) {
                    traverse(node.children);
                }
            }
        };
        traverse(treeData);
        return visibleNodes;
    };

    useEffect(() => {
        let syllabusGroupsToShow = [];
        if (brandConfig.useTestWizardSingleSyllabusGroup) {
            if (content.currentSyllabusGroup) {
                syllabusGroupsToShow = [content.currentSyllabusGroup];
            }
        } else {
            syllabusGroupsToShow = content.syllabusGroupList;
        }

        const initial = syllabusGroupsToShow
            .filter(g => g && !g.hideTest)
            .map(g => ({
                title: g.syllabusGroupName,
                key: `group-${g.syllabusGroupId}`,
                children: [],
                dataRef: { type: 'group', id: g.syllabusGroupId },
                checkable: false
            }));

        setTreeData(initial);

        if (initial.length === 1) {
            const singleGroupNode = initial[0];
            setExpandedKeys([singleGroupNode.key]);
            onLoadData(singleGroupNode);
        } else {
            setExpandedKeys([]);
        }
    }, [
        content.syllabusGroupList,
        content.currentSyllabusGroup,
        brandConfig.useTestWizardSingleSyllabusGroup
    ]);

    useEffect(() => {
        if (isTreeFocused && activeKey && treeContainerRef.current) {
            const activeNodeElement = treeContainerRef.current.querySelector(`[data-node-key="${activeKey}"]`);
            if (activeNodeElement) {
                activeNodeElement.focus({ preventScroll: true }); // preventScroll avoids page jumps
            }
        }
    }, [activeKey, isTreeFocused]);

    const onExpand = (keys) => {
        setExpandedKeys(keys);
    };

    const updateQuestionCount = useCallback(async () => {
        const nodes = checkedNodes;
        if (!nodes.length) {
            setQuestionCount(0);
            return;
        }

        const formValues = form.getFieldsValue();
        const conceptTopics = nodes.map(node => {
            const { dataRef } = node;
            return {
                syllabusId: dataRef.syllabusId || (dataRef.type === 'syllabus' ? dataRef.id : null),
                topicId: dataRef.topicId || (dataRef.type === 'topic' ? dataRef.id : null),
                conceptId: dataRef.type === 'concept' ? dataRef.id : null,
            };
        });

        const questionLevelCounts = {};
        if (brandConfig.testQuestionLevels === 1) {
            questionLevelCounts[1] = formValues.numQuestions || 0;
        } else {
            brandConfig.testQuestionLevelNames.forEach((_, idx) => {
                const levelKey = idx + 1;
                questionLevelCounts[levelKey] = formValues[`level${levelKey}`] || 0;
            });
        }

        const payload = {
            digitalOnlyQuestions: formValues.digitalOnlyQuestions || false,
            conceptTopics: conceptTopics,
            questionLevelCounts: questionLevelCounts,
        };

        try {
            setCountingQuestion(true);
            const { data } = await axios.post('/web/ui/test/teacher/wizardTest/questionCount', payload);
            setQuestionCount(data);
        } catch (error) {
            console.error('Failed to count questions', error);
            message.error('Failed to count questions');
        } finally {
            setCountingQuestion(false);
        }
    }, [brandConfig, checkedNodes, form]);

    const debouncedUpdateQuestionCount = useMemo(
        () => debounce(updateQuestionCount, 500),
        [updateQuestionCount]
    );

    useEffect(() => {
        debouncedUpdateQuestionCount();
    }, [checkedNodes, debouncedUpdateQuestionCount]);

    useEffect(() => {
        return () => {
            debouncedUpdateQuestionCount.cancel();
        };
    }, [debouncedUpdateQuestionCount]);


    useEffect(() => {
        setCanGenerate(questionCount > 0 && questionCount <= brandConfig.maxTestQuestions);
    }, [questionCount, brandConfig.maxTestQuestions]);

    const onLoadData = node => {
        const { dataRef, key, children } = node;
        if (children && children.length > 0) {
            return Promise.resolve();
        }

        setLoadingTree(true);
        let url;

        if (dataRef.type === 'group') {
            url = `/controlCentre/loadWizardSyllabuses.action?syllabusGroupId=${dataRef.id}`;
        } else if (dataRef.type === 'syllabus') {
            url = `/controlCentre/loadWizardTopics.action?syllabusId=${dataRef.id}`;
        } else if (dataRef.type === 'topic') {
            url = `/controlCentre/loadWizardConcepts.action?topicId=${dataRef.id}`;
        } else {
            setLoadingTree(false);
            return Promise.resolve();
        }

        return axios
            .get(url)
            .then(({ data }) => {
                let newChildren = [];

                if (dataRef.type === 'group') {
                    newChildren = data.syllabuses.map(item => ({
                        title: item.syllabusName,
                        key: `syllabus-${item.syllabusId}`,
                        children: [],
                        dataRef: { type: 'syllabus', id: item.syllabusId },
                        checkable: false
                    }));
                } else if (dataRef.type === 'syllabus') {
                    newChildren = data.topics
                        .filter(item => !item.hideInTest)
                        .map(item => ({
                            title: item.topicName,
                            key: `topic-${item.topicId}`,
                            isLeaf: brandConfig.useTopicTestQuestions,
                            children: [],
                            dataRef: { type: 'topic', id: item.topicId, syllabusId: dataRef.id },
                        }));
                } else {
                    newChildren = data.concepts
                        .filter(item => !item.hideInTest)
                        .map(item => ({
                            title: item.conceptName,
                            key: `concept-${item.conceptId}`,
                            isLeaf: true,
                            dataRef: {
                                type: 'concept',
                                id: item.conceptId,
                                topicId: dataRef.id,
                                syllabusId: dataRef.syllabusId,
                            },
                        }));
                }

                const updateTreeData = (list, targetKey, childNodes) => {
                    return list.map(nodeItem => {
                        if (nodeItem.key === targetKey) {
                            return { ...nodeItem, children: childNodes };
                        }
                        if (nodeItem.children) {
                            return { ...nodeItem, children: updateTreeData(nodeItem.children, targetKey, childNodes) };
                        }
                        return nodeItem;
                    });
                };

                setTreeData(prevTreeData => updateTreeData(prevTreeData, key, newChildren));
            })
            .catch(() => {
                message.error('Failed to load tree data.');
            })
            .finally(() => {
                setLoadingTree(false);
            });
    };

    const onCheck = (keys, info) => {
        setCheckedKeys(keys);
        setCheckedNodes(info.checkedNodes);
    };

    const handleKeyDown = (e) => {
        const visibleNodes = getVisibleNodes();
        if (visibleNodes.length === 0) return;

        const activeIndex = visibleNodes.findIndex(node => node.key === activeKey);
        if (activeIndex === -1) return; // Don't handle keys if nothing is active

        const activeNode = visibleNodes[activeIndex];

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                if (activeIndex < visibleNodes.length - 1) {
                    setActiveKey(visibleNodes[activeIndex + 1].key);
                }
                break;
            case 'ArrowUp':
                e.preventDefault();
                if (activeIndex > 0) {
                    setActiveKey(visibleNodes[activeIndex - 1].key);
                }
                break;
            case 'ArrowRight':
                e.preventDefault();
                if (!expandedKeys.includes(activeKey) && !activeNode.isLeaf) {
                    setExpandedKeys([...expandedKeys, activeKey]);
                    if (!activeNode.children || activeNode.children.length === 0) {
                        onLoadData(activeNode);
                    }
                } else if (activeNode.children && activeNode.children.length > 0 && activeIndex < visibleNodes.length - 1) {
                    // Move to the first child if expanded
                    setActiveKey(visibleNodes[activeIndex + 1].key);
                }
                break;
            case 'ArrowLeft':
                e.preventDefault();
                if (expandedKeys.includes(activeKey)) {
                    setExpandedKeys(expandedKeys.filter(k => k !== activeKey));
                } else {
                    // Move to parent if it exists
                    const findParent = (nodes, key, parent) => {
                        for (const node of nodes) {
                            if (node.key === key) return parent;
                            if (node.children) {
                                const found = findParent(node.children, key, node);
                                if (found) return found;
                            }
                        }
                        return null;
                    }
                    const parentNode = findParent(treeData, activeKey, null);
                    if(parentNode) {
                        setActiveKey(parentNode.key);
                    }
                }
                break;
            case ' ': // Spacebar
            case 'Enter':
                e.preventDefault();
                if (activeNode.checkable !== false) {
                    // Ant Design's onCheck can be complex with checkStrictly.
                    // This manually toggles the single node.
                    const newCheckedKeys = checkedKeys.includes(activeKey)
                        ? checkedKeys.filter(k => k !== activeKey)
                        : [...checkedKeys, activeKey];

                    // This simplified update assumes no checkStrictly logic.
                    // A full implementation would need to recursively find all nodes to check/uncheck.
                    const findNode = (nodes, key) => {
                        for (const node of nodes) {
                            if (node.key === key) return node;
                            if (node.children) {
                                const found = findNode(node.children, key);
                                if (found) return found;
                            }
                        }
                        return null;
                    }

                    const newCheckedNodes = newCheckedKeys.map(key => findNode(treeData, key)).filter(Boolean);
                    setCheckedKeys(newCheckedKeys);
                    setCheckedNodes(newCheckedNodes);
                }
                break;
            default:
                break;
        }
    };

    // --- New Accessibility Handlers ---
    const handleTreeFocus = () => {
        setIsTreeFocused(true);
        // If user tabs in and no node is active, activate the first one.
        if (!activeKey && treeData.length > 0) {
            setActiveKey(treeData[0].key);
        }
    };

    const handleTreeBlur = () => {
        setIsTreeFocused(false);
    };
    // ------------------------------------

    const onFinish = async (values) => {
        if (!canGenerate) {
            message.error('Cannot generate test: adjust selections or levels.');
            return;
        }
        setSubmitting(true);
        try {
            const formValues = form.getFieldsValue();
            const conceptTopics = checkedNodes.map(node => {
                const { dataRef } = node;
                return {
                    syllabusId: dataRef.syllabusId || (dataRef.type === 'syllabus' ? dataRef.id : null),
                    topicId: dataRef.topicId || (dataRef.type === 'topic' ? dataRef.id : null),
                    conceptId: dataRef.type === 'concept' ? dataRef.id : null,
                };
            });

            const questionLevelCounts = {};
            if (brandConfig.testQuestionLevels === 1) {
                questionLevelCounts[1] = formValues.numQuestions || 0;
            } else {
                brandConfig.testQuestionLevelNames.forEach((_, idx) => {
                    const levelKey = idx + 1;
                    questionLevelCounts[levelKey] = formValues[`level${levelKey}`] || 0;
                });
            }

            const payload = {
                testName: formValues.name,
                testDescription: formValues.description,
                digitalOnlyQuestions: formValues.digitalOnlyQuestions || false,
                conceptTopics: conceptTopics,
                questionLevelCounts: questionLevelCounts,
            };
            const { data } = await axios.post('/web/ui/test/teacher/wizardTest/createTest', payload);
            if (typeof onTestGenerated === 'function' && data != undefined) {
                onTestGenerated(data);
            }
        } catch(error) {
            console.error('Failed to load test data.', error);
            message.error('Failed to generate test.');
        } finally {
            setSubmitting(false);
        }
    };

    const renderLevelSelectors = () => {
        if (brandConfig.testQuestionLevels === 1) {
            return (
                <Form.Item
                    label={`Questions per ${brandConfig.topicName}`}
                    name="numQuestions"
                    rules={[{ required: true }]}
                >
                    <Select
                        onChange={debouncedUpdateQuestionCount}
                    >
                        {Array.from({ length: brandConfig.maxTestQuestions + 1 }, (_, n) => (
                            <Option key={n} value={n}>{n}</Option>
                        ))}
                    </Select>
                </Form.Item>
            );
        }

        return (
            <>
                <span>Number of questions per level for each selected {brandConfig.useTopicTestQuestions ? brandConfig.topicName : brandConfig.conceptName}:</span>
                <div className={'flexSpaceBetween smallGap'}>
                    {brandConfig.testQuestionLevelNames.map((levelName, idx) => (
                        <Form.Item
                            label={levelName}
                            name={`level${idx + 1}`}
                            rules={[{required: true}]}
                            key={idx}
                        >
                            <InputNumber min={0} max={15} onChange={debouncedUpdateQuestionCount}/>
                        </Form.Item>
                    ))}
                </div>
            </>
        );
    };

    const initialFormValues = {
        name: '',
        description: '',
        digitalOnlyQuestions: false,
    };

    if (brandConfig.testQuestionLevels === 1) {
        initialFormValues.numQuestions = 0;
    } else {
        brandConfig.testQuestionLevelNames.forEach((_, idx) => {
            initialFormValues[`level${idx + 1}`] = 0;
        });
    }

    const titleRender = (node) => {
        const showFocus = isTreeFocused && node.key === activeKey;
        return (
            <span
                data-node-key={node.key}
                id={`node-${node.key}`}
                role="treeitem"
                aria-selected={node.key === activeKey}
                tabIndex={-1}
                className={showFocus ? 'ch-item-focus ' : undefined}
            >
                {node.title}
            </span>
        );
    };

    return (
        <>
        {brandConfig.useReactTestEditor && <BreadcrumbBar/>}
            <div id="test-wizard" className={'margin-t--s'}>
                <Row justify="space-between" align="middle" className="wizardTitle">
                    <Col>
                        <h2 id="wizard-title" className={'ch-xl-font margin-b--m'}>{brandConfig.testName} wizard</h2>
                    </Col>
                    {!brandConfig.useReactTestEditor && <Col>
                        <Button
                            type="link"
                            icon={<ArrowLeftOutlined />}
                            onClick={onClose}
                        >
                            Back to my {brandConfig.testsName}
                        </Button>
                    </Col>}
                </Row>
                <Row gutter={24} className="wizardContent">
                    <Col xs={24} md={12}>
                        <p>
                            First give your <b>{brandConfig.testName}</b> a name,
                            choose the question level breakdown and then select your {brandConfig.syllabusName.toLowerCase()}, {brandConfig.topicsName.toLowerCase()} and {brandConfig.conceptsName.toLowerCase()}.
                        </p>
                        <p>
                            Checking a box will select all {brandConfig.topicsName.toLowerCase()}/{brandConfig.conceptsName.toLowerCase()} within the selection.
                        </p>
                        <p>
                            {brandConfig.testsName} must not exceed <b>{brandConfig.maxTestQuestions}</b> questions.
                        </p>
                        <p
                            className={'ch-med-font '+(!canGenerate ? 'ch-warning-text' : '')}
                            aria-live="polite"
                            aria-atomic="true"
                        >
                            Currently you have <b>{questionCount}</b> questions selected.
                        </p>
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={onFinish}
                            initialValues={initialFormValues}
                            disabled={treeData && treeData.length === 0}
                        >
                            <h3>{brandConfig.testName} details</h3>
                            <Form.Item
                                name="name"
                                label="Name"
                                htmlFor="edit-test-name"
                                rules={[{ required: true, message: 'Please name your test.' }]}
                            >
                                <Input id="edit-test-name" />
                            </Form.Item>

                            <Form.Item name="description" label="Description" htmlFor="test-description">
                                <Input id="test-description" maxLength={250} />
                            </Form.Item>

                            {brandConfig.digitalOnlyTestQuestions && (
                                <Form.Item name="digitalOnlyQuestions" valuePropName="checked">
                                    <Checkbox onChange={debouncedUpdateQuestionCount} id="digital-only">
                                        Digital-only questions
                                    </Checkbox>
                                </Form.Item>
                            )}

                            <h3>Question difficulties</h3>
                            {renderLevelSelectors()}

                            <Form.Item style={{ marginTop: '20px' }}>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={submitting}
                                    disabled={!canGenerate || countingQuestion || submitting}
                                >
                                    Generate test
                                </Button>
                            </Form.Item>
                        </Form>
                    </Col>

                    <Col xs={24} md={12}>
                        {(treeData && treeData.length === 0) &&
                        <div className={'ch-warning-text'}>No {brandConfig.testName} questions available</div>
                    }
                    {(treeData && treeData.length > 0) &&
                        <><h3 id="topic-concept-heading">
                            {brandConfig.topicName}/{brandConfig.conceptName} select <CircularLoader pageCentered={false} displayInline={true} visible={submitting || loadingTree} />
                        </h3>
                        <div
                            ref={treeContainerRef}
                            onKeyDown={handleKeyDown}
                            onFocus={handleTreeFocus}
                            onBlur={handleTreeBlur}
                            tabIndex={0}
                            role="tree"
                            aria-labelledby="topic-concept-heading"
                            aria-activedescendant={activeKey ? `node-${activeKey}` : undefined}
                            style={{ outline: 'none' }} // Hide browser's default focus on the container
                        >
                            <Tree
                                checkable
                                loadData={onLoadData}
                                treeData={treeData}
                                checkedKeys={checkedKeys}
                                onCheck={onCheck}
                                expandedKeys={expandedKeys}
                                onExpand={onExpand}
                                titleRender={titleRender}
                                onSelect={(_, { node }) => setActiveKey(node.key)}
                            />
                        </div></>
                    }
                    </Col>
                </Row>
            </div>
        </>
    );
};

export default TestWizard;